import React, { useState, useEffect } from 'react';
import {
  HiOutlinePlus,
  HiOutlineMagnifyingGlass,
  HiOutlineClipboardDocumentList,
  HiOutlinePencil,
  HiOutlineTrash,
  HiOutlineXMark
} from 'react-icons/hi2';
import { authService } from '../../../services';
import masterDataService from '../../../services/masterDataService';
import SharedModal from '../../../components/common/SharedModal';
import PageLoading from "../../../components/common/PageLoading";
import "../PlannedMaintenanceMasters.css";

const MaintenanceSlaPage = () => {
  const [slas, setSlas] = useState([]);
  const [priorities, setPriorities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingSla, setEditingSla] = useState(null);
  const [formData, setFormData] = useState({
    priorityId: '',
    responseTimeHours: '',
    resolutionTimeHours: '',
  });
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const tenantId = authService.getTenantId();

  useEffect(() => {
    if (tenantId) {
      loadData();
    } else {
      setError('Please login to view maintenance SLA');
      setLoading(false);
    }
  }, [tenantId]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');
      const [slasData, prioritiesData] = await Promise.all([
        masterDataService.getMaintenanceSla(tenantId),
        masterDataService.getWorkOrderPriorities(tenantId)
      ]);
      setSlas(slasData || []);
      setPriorities(prioritiesData || []);
    } catch (err) {
      setError('Failed to load data. Please try again.');
      console.error('Error loading data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const filteredSlas = slas.filter((sla) => {
    const priority = priorities.find(p => p.priorityId === sla.priorityId);
    return priority?.priorityName?.toLowerCase().includes(searchTerm.toLowerCase());
  });

  const handleAdd = () => {
    setEditingSla(null);
    setFormData({
      priorityId: '',
      responseTimeHours: '',
      resolutionTimeHours: '',
    });
    setFormErrors({});
    setShowModal(true);
  };

  const handleEdit = (sla) => {
    setEditingSla(sla);
    setFormData({
      priorityId: sla.priorityId?.toString() || '',
      responseTimeHours: sla.responseTimeHours?.toString() || '',
      resolutionTimeHours: sla.resolutionTimeHours?.toString() || '',
    });
    setFormErrors({});
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    try {
      setError('');
      // Note: deleteFn is null in config, so we'll skip delete for now
      setError('Delete functionality is not available for maintenance SLA.');
      setDeleteConfirm(null);
    } catch (err) {
      setError('Failed to delete maintenance SLA. Please try again.');
      console.error('Error deleting maintenance SLA:', err);
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.priorityId) {
      errors.priorityId = 'Priority is required';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      setSubmitting(true);
      setError('');

      const slaData = {
        tenantId: tenantId,
        priorityId: parseInt(formData.priorityId),
        responseTimeHours: formData.responseTimeHours ? parseFloat(formData.responseTimeHours) : null,
        resolutionTimeHours: formData.resolutionTimeHours ? parseFloat(formData.resolutionTimeHours) : null,
      };

      if (editingSla) {
        slaData.slaId = editingSla.slaId;
        await masterDataService.updateMaintenanceSla(editingSla.slaId, slaData);
      } else {
        await masterDataService.createMaintenanceSla(slaData);
      }

      await loadData();
      setShowModal(false);
      setFormData({ priorityId: '', responseTimeHours: '', resolutionTimeHours: '' });
      setEditingSla(null);
    } catch (err) {
      const errorMessage = err.response?.data?.message || 
                          err.response?.data?.errors || 
                          err.message || 
                          'Failed to save maintenance SLA. Please try again.';
      setError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingSla(null);
    setFormData({ priorityId: '', responseTimeHours: '', resolutionTimeHours: '' });
    setFormErrors({});
  };

  const getPriorityName = (priorityId) => {
    if (!priorityId) return '-';
    const priority = priorities.find(p => p.priorityId === priorityId);
    return priority ? priority.priorityName : '-';
  };

  if (loading) {
    return (
      <div className="pm-masters-page">
        <PageLoading message="Loading maintenance SLA..." />
      </div>
    );
  }

  return (
    <div className="pagecontainer pm-masters-page">
      <div className="page-header">
        <div className="header-content">
          <h1>Maintenance SLA</h1>
          <p>Manage maintenance SLA for Planned Maintenance</p>
        </div>
        <button className="btn-primary" onClick={handleAdd}>
          <HiOutlinePlus />
          Add SLA
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="table-container">
        <div className="table-header">
          <div className="search-box">
            <HiOutlineMagnifyingGlass className="search-icon" />
            <input
              type="text"
              placeholder="Search SLA..."
              value={searchTerm}
              onChange={handleSearch}
              className="search-input"
            />
          </div>
          <div className="table-info">
            {filteredSlas.length} {filteredSlas.length === 1 ? 'SLA' : 'SLAs'}
          </div>
        </div>

        {filteredSlas.length === 0 ? (
          <div className="empty-state">
            <HiOutlineClipboardDocumentList className="empty-icon" />
            <h3>No SLA found</h3>
            <p>
              {searchTerm
                ? 'Try adjusting your search terms'
                : 'Get started by creating your first maintenance SLA'}
            </p>
            {!searchTerm && (
              <button className="btn-primary" onClick={handleAdd}>
                Add SLA
              </button>
            )}
          </div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Priority</th>
                <th>Response Time (hrs)</th>
                <th>Resolution Time (hrs)</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredSlas.map((sla) => (
                <tr key={sla.slaId}>
                  <td>{sla.slaId}</td>
                  <td className="name-cell">{getPriorityName(sla.priorityId)}</td>
                  <td>{sla.responseTimeHours ?? '-'}</td>
                  <td>{sla.resolutionTimeHours ?? '-'}</td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="btn-icon-edit"
                        onClick={() => handleEdit(sla)}
                        title="Edit"
                      >
                        <HiOutlinePencil />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <SharedModal
        isOpen={showModal}
        onClose={handleCloseModal}
        title={editingSla ? 'Edit Maintenance SLA' : 'Add New Maintenance SLA'}
        footer={
          <>
            <button
              type="button"
              className="btn-secondary"
              onClick={handleCloseModal}
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              form="sla-form"
              className="btn-primary"
              disabled={submitting}
            >
              {submitting
                ? editingSla
                  ? 'Updating...'
                  : 'Creating...'
                : editingSla
                ? 'Update'
                : 'Create'}
            </button>
          </>
        }
      >
        <div className="shared-modal-body">
          <form id="sla-form" onSubmit={handleSubmit} className="modal-form">
            <div className="form-group">
              <label htmlFor="priorityId">
                Priority <span className="required">*</span>
              </label>
              <select
                id="priorityId"
                value={formData.priorityId}
                onChange={(e) =>
                  setFormData({ ...formData, priorityId: e.target.value })
                }
                className={formErrors.priorityId ? 'error' : ''}
              >
                <option value="">Select Priority</option>
                {priorities.map((priority) => (
                  <option key={priority.priorityId} value={priority.priorityId.toString()}>
                    {priority.priorityName}
                  </option>
                ))}
              </select>
              {formErrors.priorityId && (
                <span className="field-error">{formErrors.priorityId}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="responseTimeHours">Response Time (hours)</label>
              <input
                type="number"
                id="responseTimeHours"
                value={formData.responseTimeHours}
                onChange={(e) =>
                  setFormData({ ...formData, responseTimeHours: e.target.value })
                }
                placeholder="Enter response time in hours (optional)"
                step="0.1"
              />
            </div>

            <div className="form-group">
              <label htmlFor="resolutionTimeHours">Resolution Time (hours)</label>
              <input
                type="number"
                id="resolutionTimeHours"
                value={formData.resolutionTimeHours}
                onChange={(e) =>
                  setFormData({ ...formData, resolutionTimeHours: e.target.value })
                }
                placeholder="Enter resolution time in hours (optional)"
                step="0.1"
              />
            </div>
          </form>
        </div>
      </SharedModal>
    </div>
  );
};

export default MaintenanceSlaPage;
